import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { FileDown, Loader2, Eye } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface ReportPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportName: string;
  tables: string[];
  columns: Record<string, string[]>;
  dateRange?: { startDate: string; endDate: string };
  onExport: (format: 'pdf' | 'excel') => void;
}

export function ReportPreviewDialog({
  open,
  onOpenChange,
  reportName,
  tables,
  columns,
  dateRange,
  onExport,
}: ReportPreviewDialogProps) {
  const [previewData, setPreviewData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [totalCounts, setTotalCounts] = useState<Record<string, number>>({});

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const data: Record<string, any[]> = {};
      const counts: Record<string, number> = {};

      for (const table of tables) {
        const tableColumns = columns[table];
        if (!tableColumns || tableColumns.length === 0) continue;

        // Get count first
        const { count } = await supabase
          .from(table as any)
          .select('*', { count: 'exact', head: true });
        
        counts[table] = count || 0;

        // Get preview data (limit to 5 rows)
        let query = supabase.from(table as any).select(tableColumns.join(', ')).limit(5);

        // Apply date filter if provided
        if (dateRange?.startDate && dateRange?.endDate) {
          const dateColumn = tableColumns.includes('created_at') ? 'created_at' 
            : tableColumns.includes('start_date') ? 'start_date'
            : tableColumns.includes('loss_date') ? 'loss_date'
            : null;
          
          if (dateColumn) {
            query = query.gte(dateColumn, dateRange.startDate).lte(dateColumn, dateRange.endDate);
          }
        }

        const { data: tableData, error } = await query;

        if (error) {
          console.error(`Error fetching preview for ${table}:`, error);
          continue;
        }

        data[table] = tableData || [];
      }

      setPreviewData(data);
      setTotalCounts(counts);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast({ title: "Failed to load preview", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      fetchPreview();
    } else {
      setPreviewData({});
      setTotalCounts({});
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Report Preview: {reportName}
          </DialogTitle>
          <DialogDescription>
            Preview of the first 5 rows from each table. Export to get the full dataset.
            {dateRange && (
              <span className="ml-2">
                <Badge variant="outline">
                  {dateRange.startDate} to {dateRange.endDate}
                </Badge>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {tables.map((table) => {
                const data = previewData[table] || [];
                const tableColumns = columns[table] || [];
                const totalCount = totalCounts[table] || 0;

                if (tableColumns.length === 0) return null;

                return (
                  <div key={table} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold capitalize">
                        {table.replace(/_/g, ' ')}
                      </h3>
                      <Badge variant="secondary">
                        {totalCount} total records
                      </Badge>
                    </div>

                    {data.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">
                        No data available for this table
                      </p>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {tableColumns.map((col) => (
                                <TableHead key={col} className="text-xs">
                                  {col.replace(/_/g, ' ')}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.map((row, idx) => (
                              <TableRow key={idx}>
                                {tableColumns.map((col) => (
                                  <TableCell key={col} className="text-xs">
                                    {row[col] === null || row[col] === undefined
                                      ? '-'
                                      : typeof row[col] === 'object'
                                      ? JSON.stringify(row[col]).substring(0, 30)
                                      : String(row[col]).substring(0, 30)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="outline" onClick={() => onExport('excel')}>
            <FileDown className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={() => onExport('pdf')}>
            <FileDown className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}